import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import useAppDispatch from 'hooks/common/useAppDispatch';
import terms from 'i18n';
import { ConfirmCode } from 'models/auth';
import { createToast } from 'redux/actions';
import { useEmailMutation, useCodeMutation } from 'redux/api/common/auth-api';

type LocationState = { email?: string };

const useSignUpCodeForm = () => {
  const [getCode] = useCodeMutation();
  const [sendEmail] = useEmailMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const emailFromState = state?.email || '';
  const dispatch = useAppDispatch();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [countdown, setCountdown] = useState(() => {
    const saved = localStorage.getItem('countdown');
    const value = saved ? parseInt(saved, 10) : 60;
    return value;
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ConfirmCode>({
    mode: 'onChange',
    defaultValues: {
      email: emailFromState,
    },
  });

  useEffect(() => {
    if (!emailFromState) {
      navigate('/sign-up/email');
    }
  }, [emailFromState]);

  const fields = {
    email: register('email', {
      required: terms.REQUIRED_FIELD,
    }),
    code: register('code', {
      required: terms.REQUIRED_FIELD,
    }),
  };

  const onSubmit = handleSubmit(async (data: ConfirmCode) => {
    try {
      const isValid = await getCode({
        email: data.email,
        code: data.code,
      }).unwrap();
      if (!isValid) {
        dispatch(createToast(terms.ERROR_CODE, 'danger'));
        return;
      }
      navigate('/sign-up', { state: { email: data.email } });
    } catch (error: any) {
      const status = error?.status;
      const headers = error?.meta?.response?.headers;

      if (status === 429) {
        const retryAfter = headers?.get('Retry-After');
        const seconds = Number(retryAfter) || 60;
        setSubmitDisabled(true);
        setTimeout(() => setSubmitDisabled(false), seconds * 1000);
        dispatch(createToast(terms.TOO_MANY_ATTEMPTS, 'danger'));
      } else {
        dispatch(createToast(terms.EMAIL_ERROR, 'danger'));
      }
    }
  });

  const handleResend = async () => {
    const email = getValues('email');
    if (!email) return;

    try {
      await sendEmail({ email }).unwrap();
      dispatch(createToast(terms.EMAIL_SUCCESS, 'success'));
      localStorage.setItem('countdown', '10');
      setCountdown(60);
      setBtnDisabled(true);
    } catch (error: any) {
      dispatch(createToast('Internal error', 'danger'));
    }
  };

  useEffect(() => {
    if (countdown === 0) {
      setBtnDisabled(false);
      localStorage.removeItem('countdown');
      return;
    }
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          localStorage.removeItem('countdown');
          setBtnDisabled(false);
          return 0;
        }
        const newValue = prev - 1;
        localStorage.setItem('countdown', newValue.toString());
        return newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return {
    fields,
    onSubmit,
    errors,
    handleResend,
    btnDisabled,
    submitDisabled,
    countdown,
  };
};

export default useSignUpCodeForm;
